import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { InviteRole, TeamCapability } from '@ghostscreen/contracts';
import { StatCard } from '../components/dashboard/StatCard';
import { StatsGrid } from '../components/dashboard/StatsGrid';
import { apiErrorMessage } from '../i18n';
import { api } from '../lib/api';
import { formatCount } from '../lib/dashboard/formatCount';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { TeamsInviteCard } from './teams/TeamsInviteCard';
import { TeamsListCard } from './teams/TeamsListCard';
import { TeamsPendingInvitesCard } from './teams/TeamsPendingInvitesCard';
import { TeamsPeopleCard } from './teams/TeamsPeopleCard';
import type { PeopleRoleFilter } from './teams/teamFormat';

/**
 * People, roles, and access — teams are people groupings with capability tags
 * and optional location scope (distinct from screen groups).
 */
export function TeamsPage() {
  const { t } = useTranslation('teams');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const [peopleFilter, setPeopleFilter] = useState<PeopleRoleFilter>('all');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createGeneration, setCreateGeneration] = useState(0);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);

  const orgsQuery = useQuery({
    queryKey: ['my-organisations'],
    queryFn: api.listMyOrganisations,
  });
  const membersQuery = useQuery({
    queryKey: ['org-members', organisationId],
    queryFn: () => api.listOrganisationMembers(organisationId),
  });
  const teamsQuery = useQuery({
    queryKey: ['teams', organisationId],
    queryFn: () => api.listTeams(organisationId),
  });
  const invitesQuery = useQuery({
    queryKey: ['org-invites', organisationId],
    queryFn: () => api.listInvites(organisationId),
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => api.listLocations(organisationId),
  });

  const membersRetry = useQueryRetry(() => membersQuery.refetch());
  const teamsRetry = useQueryRetry(() => teamsQuery.refetch());
  const invitesRetry = useQueryRetry(() => invitesQuery.refetch());

  const currentRole = orgsQuery.data?.find((org) => org.id === organisationId)?.role;
  const canManageAccess =
    currentRole === 'organisation_owner' ||
    currentRole === 'organisation_admin' ||
    currentRole === 'platform_admin';

  const teamNamesByUserId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const team of teamsQuery.data ?? []) {
      for (const userId of team.memberUserIds) {
        const current = map.get(userId) ?? [];
        current.push(team.name);
        map.set(userId, current);
      }
    }
    return map;
  }, [teamsQuery.data]);

  const ownerCount =
    membersQuery.data?.filter((member) => member.role === 'organisation_owner').length ?? 0;
  const adminCount =
    membersQuery.data?.filter((member) => member.role === 'organisation_admin').length ?? 0;
  const locationScopedTeams =
    teamsQuery.data?.filter((team) => team.scope === 'locations').length ?? 0;
  const pendingInvites = invitesQuery.data?.length ?? 0;
  const oldestInviteDays = useMemo(() => {
    if (!invitesQuery.data?.length) return null;
    const oldest = invitesQuery.data.reduce((min, invite) => {
      const created = new Date(invite.createdAt).getTime();
      return created < min ? created : min;
    }, Date.now());
    return Math.max(0, Math.floor((Date.now() - oldest) / 86_400_000));
  }, [invitesQuery.data]);

  const createTeam = useMutation({
    mutationFn: (input: {
      name: string;
      scope: 'all_locations' | 'locations';
      locationIds: string[];
      capabilities: TeamCapability[];
      memberUserIds: string[];
    }) => api.createTeam(organisationId, input),
    onSuccess: async () => {
      setCreateError(null);
      setCreateGeneration((value) => value + 1);
      await queryClient.invalidateQueries({ queryKey: ['teams', organisationId] });
    },
    onError: (err) => {
      setCreateError(apiErrorMessage(err, t('page.createError')));
    },
  });

  const deleteTeam = useMutation({
    mutationFn: (teamId: string) => api.deleteTeam(organisationId, teamId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['teams', organisationId] });
    },
  });

  const sendInvites = useMutation({
    mutationFn: (input: { emails: string[]; role: InviteRole }) =>
      api.createInvites(organisationId, input),
    onSuccess: async (created) => {
      setInviteError(null);
      setInviteSuccess(
        created.length === 1
          ? t('page.inviteRecordedFor', { email: created[0]!.email })
          : t('page.invitesRecorded', { count: created.length }),
      );
      await queryClient.invalidateQueries({ queryKey: ['org-invites', organisationId] });
    },
    onError: (err) => {
      setInviteSuccess(null);
      setInviteError(apiErrorMessage(err, t('page.inviteError')));
    },
  });

  const resendInvite = useMutation({
    mutationFn: (inviteId: string) => api.resendInvite(organisationId, inviteId),
    onMutate: (inviteId) => setBusyInviteId(inviteId),
    onSettled: () => setBusyInviteId(null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['org-invites', organisationId] });
    },
  });

  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) => api.revokeInvite(organisationId, inviteId),
    onMutate: (inviteId) => setBusyInviteId(inviteId),
    onSettled: () => setBusyInviteId(null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['org-invites', organisationId] });
    },
  });

  return (
    <div className="space-y-6">
      <StatsGrid>
        <StatCard
          label={t('page.stats.people')}
          value={formatCount(membersQuery.data?.length, membersQuery.isPending)}
          note={membersQuery.isPending ? undefined : t('page.stats.peopleNote')}
        />
        <StatCard
          label={t('page.stats.teams')}
          value={formatCount(teamsQuery.data?.length, teamsQuery.isPending)}
          note={
            teamsQuery.isPending
              ? undefined
              : t('locationScoped', { count: locationScopedTeams })
          }
        />
        <StatCard
          label={t('page.stats.pendingInvites')}
          value={formatCount(invitesQuery.data?.length, invitesQuery.isPending)}
          note={
            invitesQuery.isPending || pendingInvites === 0
              ? undefined
              : oldestInviteDays === 0
                ? t('page.stats.newestToday')
                : t('page.stats.oldestDays', { count: oldestInviteDays ?? 0 })
          }
        />
        <StatCard
          label={t('page.stats.owners')}
          value={formatCount(ownerCount, membersQuery.isPending)}
          note={
            membersQuery.isPending
              ? undefined
              : t('page.stats.adminsNote', { count: adminCount })
          }
        />
      </StatsGrid>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="flex flex-col gap-6 lg:col-span-3">
          <TeamsPeopleCard
            members={membersQuery.data}
            teamNamesByUserId={teamNamesByUserId}
            filter={peopleFilter}
            onFilterChange={setPeopleFilter}
            isPending={membersQuery.isPending}
            isError={membersQuery.isError}
            isRetrying={membersRetry.isRetrying}
            onRetry={membersRetry.retry}
          />
          <TeamsPendingInvitesCard
            invites={invitesQuery.data}
            isPending={invitesQuery.isPending}
            isError={invitesQuery.isError}
            isRetrying={invitesRetry.isRetrying}
            onRetry={invitesRetry.retry}
            canManage={canManageAccess}
            busyInviteId={busyInviteId}
            onResend={(inviteId) => resendInvite.mutate(inviteId)}
            onRevoke={(inviteId) => revokeInvite.mutate(inviteId)}
          />
        </div>

        <div className="flex flex-col gap-6 lg:col-span-2">
          <TeamsListCard
            teams={teamsQuery.data}
            members={membersQuery.data}
            locations={locationsQuery.data}
            isPending={teamsQuery.isPending}
            isError={teamsQuery.isError}
            isRetrying={teamsRetry.isRetrying}
            onRetry={teamsRetry.retry}
            creating={createTeam.isPending}
            createError={createError}
            createGeneration={createGeneration}
            canManage={canManageAccess}
            onCreate={(input) => {
              setCreateError(null);
              createTeam.mutate(input);
            }}
            deletingId={deleteTeam.isPending ? deleteTeam.variables ?? null : null}
            onDelete={(teamId) => deleteTeam.mutate(teamId)}
          />
          {canManageAccess ? (
            <TeamsInviteCard
              sending={sendInvites.isPending}
              error={inviteError}
              success={inviteSuccess}
              onSend={(input) => {
                setInviteError(null);
                setInviteSuccess(null);
                sendInvites.mutate(input);
              }}
            />
          ) : (
            <section
              className="rounded-[14px] border border-line px-5 py-5"
              style={{ background: 'var(--gs-panel)' }}
            >
              <h2 className="m-0 text-base font-bold text-ink">{t('invite.title')}</h2>
              <p className="m-0 mt-1 text-xs text-muted">{t('invite.restricted')}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
